import queryString from 'query-string';
import { get } from 'lodash';

import * as Error404Actions from 'rebook/apps/base/actions/Error404Actions';
import fetchQuery from 'rebook/apps/utils/fetchQuery';
import {
  CATALOG_BOOKSET_DETAIL_SET_DATA,
  CATALOG_BOOKSET_DETAIL_RESET_DATA,
} from 'rebook/apps/catalog/constants';
import * as SeoActions from 'rebook/apps/base/actions/SeoActions';
import cropStorageImage from 'rebook/apps/utils/urls/cropStorageImage';
import pluralize from 'rebook/apps/utils/text/pluralize';
import truncateChars from 'rebook/apps/utils/text/truncateChars';


export function fetchInitialData(match, location = {}, paginateBy) {
  return async (dispatch, getState) => {
    const { ssr, booksetDetail: currentState } = getState();
    const pageNumber = parseInt(get(ssr, 'query.page', 1), 10);

    const { booksetId } = match.params;

    let newState = {
      match,
      paginateBy,
      booksetId,
    };
    // obtain partial state from router (client side only)
    if (location && location.state) {
      newState = {
        ...newState,
        ...location.state,
      };
    }
    if (currentState.bookset) {
      newState = {
        ...newState,
        ...currentState,
      };
    }

    dispatch(setData(newState));

    await dispatch(fetchBookList(match, pageNumber));

    // only fetch bookset detail if we dont have it already in the initial state
    if (!newState.bookset || newState.bookset.description === undefined || !newState.rubrics) {
      await dispatch(fetchDetail(match));
    }
    // same for the books
    if (!newState.books || currentState.pageNumber !== pageNumber) {
      await dispatch(fetchBookList(match, pageNumber));
    }
  };
}

const fetchRubrics = (rubricId, ssr, booksetId) => {
  if (!rubricId) {
    return [];
  }

  const qs = {
    rubrics: rubricId,
    limit: 10,
    booklimit: 0,
    id__ne: booksetId,
  };
  const queryParams = queryString.stringify(qs);
  const url = `/api/booksets/?${queryParams}`;

  return fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
    .then(({ data }) => {
      const rubrics = get(data, 'objects', []);
      return rubrics;
    })
    .catch(({ data, resp, error }) => {
      if (resp.status === 404) {
        dispatch(Error404Actions.show());
      }
      else {
        Raven.captureMessage('failed to load bookset rubrics due to server error', {
          level: 'error',
          extra: {
            status: resp.status,
            response: data,
            error,
          },
        });
        throw new Error('failed to load bookset rubrics due to server error');
      }
    });
};

export function fetchDetail(match) {
  return async (dispatch, getState) => {
    const { ssr } = getState();
    const { booksetId } = match.params;
    const url = `/api/booksets/${booksetId}/?booklimit=0`;
    return fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
      .then(({ data }) => {
        const rubricId = get(data, 'rubrics[0].id', null);
        dispatch(setData({
          bookset: data,
        }));
        return fetchRubrics(rubricId, ssr, booksetId);
      }).then((rubrics) => {
        if (rubrics.length) {
          dispatch(setData({
            rubrics,
          }));
        }
      })
      .catch(({ data, resp, error }) => {
        if (resp.status === 404) {
          dispatch(Error404Actions.show());
        }
        else {
          Raven.captureMessage('failed to load bookset detail due to server error', {
            level: 'error',
            extra: {
              status: resp.status,
              response: data,
              error,
            },
          });
          throw new Error('failed to load bookset detail due to server error');
        }
      });
  };
}

export function fetchBookList(match, pageNumber = 1) {
  return async (dispatch, getState) => {
    const { booksetId } = match.params;
    const { ssr, booksetDetail } = getState();
    const { paginateBy, bookset } = booksetDetail;
    if (!bookset) {
      return null;
    }
    let queryParams = {
      limit: paginateBy,
    };

    if (pageNumber > 1) {
      queryParams.offset = (pageNumber - 1) * paginateBy;
    }
    const qs = queryString.stringify(queryParams);
    const url = `/api/booksets/${booksetId}/books/?${qs}`;
    // clear the book list before the fetch
    dispatch(setData({
      books: null,
      // optimistically update the UI
      pageNumber,
    }));
    return fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
      .then(
        ({ data }) => {
          const lastPageNumber = Math.ceil(data.meta.total_count / paginateBy);
          dispatch(setData({
            books: data.objects,
            lastPageNumber,
          }));
        },
      )
      .catch(
        ({ data, resp, error }) => {
          Raven.captureMessage('failed to load bookset books due to server error', {
            level: 'error',
            extra: {
              status: resp.status,
              response: data,
              error,
            },
          });
          throw new Error('failed to load bookset books due to server error');
        },
      );
  };
}

export function setSeoData() {
  return (dispatch, getState) => {
    const { booksetDetail, booksPerSubscriptionCounter } = getState();
    const { bookset } = booksetDetail;
    const picture1200x630 = cropStorageImage(bookset.picture, 1200, 630);
    const picture560x300 = cropStorageImage(bookset.picture, 560, 300);
    const title = bookset.name;
    const ogDescription = bookset.description ? bookset.description.replace(/(<([^>]+)>)/ig, '') : '';

    // https://stackoverflow.com/questions/822452/strip-html-from-text-javascript
    const description = bookset.description ?
      `Подборка ${bookset.name} - ${truncateChars(bookset.description.replace(/<[^>]*>?/gm, ''), 80)}`
      : `В электронной библиотеке MyBook можно прочитать более ${booksPerSubscriptionCounter.total}` +
      ` электронных книг. Для онлайн чтения доступны шедевры современной и классической литературы. ` +
      `Новинки и бестселлеры, отзывы, рекомендации, популярные авторы.`;
    const ogTitle = `${bookset.name}: ${bookset.active_book_count} ` +
      `${pluralize(bookset.active_book_count, 'книга', 'книги', 'книг')}`;
    const desktopUrl = `https://${BASE_HOST}${bookset.absolute_url}`;
    const ogTags = [
      { name: 'image', value: picture1200x630 },
      { name: 'title', value: bookset.seo_og_title ? bookset.seo_og_title : ogTitle },
      { name: 'description', value: bookset.seo_og_description ? bookset.seo_og_description : ogDescription },
      { name: 'url', value: desktopUrl },
    ];
    const ogTwitterTags = [
      { name: 'card', value: 'summary_large_image' },
      { name: 'title', value: ogTitle },
      { name: 'description', value: ogDescription },
      { name: 'image', value: picture560x300 },
      { name: 'image:width', value: '280px' },
      { name: 'image:height', value: '150px' },
    ];
    dispatch(SeoActions.setData({
      imageSrc: picture1200x630,
      canonicalUrl: bookset.seo_meta_canonical ? bookset.seo_meta_canonical : desktopUrl,
      title: bookset.seo_meta_title ? bookset.seo_meta_title : title,
      description: bookset.seo_meta_description ? bookset.seo_meta_description : description,
      robots: bookset.seo_meta_robots,
      ogTags,
      ogTwitterTags,
    }));
  };
}

export function setData(payload) {
  return (dispatch) => {
    const { bookset } = payload;
    dispatch({
      type: CATALOG_BOOKSET_DETAIL_SET_DATA,
      payload,
    });
    if (bookset) {
      dispatch(setSeoData());
    }
  };
}

export function resetData() {
  return (dispatch) => {
    dispatch({
      type: CATALOG_BOOKSET_DETAIL_RESET_DATA,
    });
    dispatch(SeoActions.resetData());
  };
}
